import { useCallback, useState } from 'react';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useDeleteLiveStream,
  useForbidLiveStream,
  useResetLiveStream,
} from '@/api/media/live-live-streams';
import type { LiveStreamItem } from '@/api/media/live-live-streams-types';

export type ActionKind = 'delete' | 'forbid' | 'resume';

export interface PendingAction {
  kind: ActionKind;
  row: LiveStreamItem;
}

export function confirmProps(action: PendingAction): {
  title: string;
  description: string;
  confirmLabel: string;
  destructive: boolean;
  requireText?: string;
} {
  const name = action.row.stream_name;
  if (action.kind === 'delete') {
    return {
      title: 'Delete Job',
      description: `Permanently delete job "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      requireText: name,
    };
  }
  if (action.kind === 'forbid') {
    return {
      title: 'Forbid Job',
      description: `Prevent new ingest connections for "${name}"?`,
      confirmLabel: 'Forbid',
      destructive: false,
    };
  }
  return {
    title: 'Resume Job',
    description: `Re-enable ingest for "${name}"?`,
    confirmLabel: 'Resume',
    destructive: false,
  };
}

interface UseJobActionsOptions {
  onDeleteSuccess?: () => void;
}

/** Shared mutation + confirm state for list and detail pages. */
export function useJobActions(
  serviceId: string,
  options?: UseJobActionsOptions,
) {
  const toast = useToast();
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const deleteMut = useDeleteLiveStream(serviceId, {
    onSuccess: () => {
      toast.success('Job deleted');
      setPendingAction(null);
      options?.onDeleteSuccess?.();
    },
    onError: (err) => toast.error((err as { message?: string })?.message || 'Failed to delete job'),
  });

  const forbidMut = useForbidLiveStream(serviceId, {
    onSuccess: () => { toast.success('Job forbidden'); setPendingAction(null); },
    onError: (err) => toast.error((err as { message?: string })?.message || 'Failed to forbid job'),
  });

  const resumeMut = useResetLiveStream(serviceId, {
    onSuccess: () => { toast.success('Job resumed'); setPendingAction(null); },
    onError: (err) => toast.error((err as { message?: string })?.message || 'Failed to resume job'),
  });

  const handleConfirm = useCallback(() => {
    if (!pendingAction) return;
    if (pendingAction.kind === 'delete') deleteMut.mutate(pendingAction.row.id);
    else if (pendingAction.kind === 'forbid') forbidMut.mutate(pendingAction.row.id);
    else resumeMut.mutate(pendingAction.row.id);
  }, [pendingAction, deleteMut, forbidMut, resumeMut]);

  const isBusy = deleteMut.isPending || forbidMut.isPending || resumeMut.isPending;

  return { pendingAction, setPendingAction, handleConfirm, isBusy };
}
